import { DEFAULT_DELIVERY_FEE, HORS_DAKAR_DELIVERY_FEE } from '../constants/deliveryStatus';

/**
 * Source unique du tarif de livraison.
 *
 * Le calcul existait auparavant en huit exemplaires — marchand, versements,
 * finances, expédition — qui divergeaient : certains ignoraient les tarifs
 * propres à la boutique et retombaient sur la constante, d'autres appliquaient
 * le tarif Dakar à des expéditions. Un marchand voyait donc des frais
 * différents de ceux qu'on lui facturait réellement.
 *
 * Toute évolution tarifaire passe désormais par ce seul fichier.
 */

/** Convertit en nombre sûr ; null, '' et NaN deviennent 0 */
function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Une livraison est hors Dakar si le drapeau `expedition` le dit.
 *
 * On n'utilise PAS l'absence de livreur comme indice : plus de mille
 * livraisons Dakar historiques n'ont jamais eu de livreur assigné, et les
 * facturer au tarif expédition surfacturait les boutiques dont les deux
 * tarifs diffèrent.
 */
export function isHorsDakar(delivery) {
  return Boolean(delivery?.expedition);
}

/**
 * Tarif applicable à une livraison, par ordre de priorité :
 *   1. le frais figé sur la livraison — c'est la vérité historique, il a
 *      servi de base au versement déjà effectué ;
 *   2. le tarif négocié de la boutique, Dakar ou expédition ;
 *   3. la constante globale, uniquement si la boutique n'a rien de défini.
 *
 * @param {object} delivery  la livraison
 * @param {object} storeFees { fee_dakar, fee_expedition } de la boutique
 */
export function resolveDeliveryFee(delivery, storeFees = null) {
  const locked = toNumber(delivery?.delivery_fee);
  if (locked > 0) return locked;

  const horsDakar = isHorsDakar(delivery);

  if (storeFees) {
    const negocie = horsDakar ? storeFees.fee_expedition : storeFees.fee_dakar;
    if (negocie !== null && negocie !== undefined && toNumber(negocie) > 0) {
      return toNumber(negocie);
    }
  }

  return horsDakar ? HORS_DAKAR_DELIVERY_FEE : DEFAULT_DELIVERY_FEE;
}

/** Somme des frais d'une liste de livraisons */
export function sumDeliveryFees(deliveries, storeFees = null) {
  return (deliveries || []).reduce(
    (total, delivery) => total + resolveDeliveryFee(delivery, storeFees),
    0,
  );
}

/**
 * Construit la table boutique -> tarifs à partir des profils marchands,
 * pour les écrans admin qui traitent plusieurs boutiques à la fois.
 */
export function buildStoreFeesMap(merchantProfiles) {
  const map = {};
  (merchantProfiles || []).forEach((profile) => {
    if (profile?.store_name) {
      map[profile.store_name] = {
        fee_dakar: profile.fee_dakar,
        fee_expedition: profile.fee_expedition,
      };
    }
  });
  return map;
}
